import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { addGroup, fetchGroupDetails, fetchGroupProjectsCount, fetchGroups, removeGroup, removeMemberFromGroup, updateGroupDetails, updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { removeLocalMemberFromGroup, updateMemberRoleInGroupStore } from "@store/groups/groups-slice-utils";

/**
 * State of groups
 */

/** Creates an entity adapter to store a map with all the groups that belong to the company. */
export const groupsAdapter = createEntityAdapter();
const initialState = {
  ...groupsAdapter.getInitialState(),
  selectedGroupId: null,
  projectsCount: {},
  projectsCountRequestIds: {},
  fetching: {
    isFetchingGroups: false,
    processingGroups: {},
    isRemovingGroupMember: false,
    isRemovingGroup: false,
    isUpdatingGroups: false
  }
};

/**
 * Slice to access state of loaded groups
 */
const groupsSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {
    /**
     * Accepts a single group entity and adds or replaces it.
     *
     * @param state store state
     * @param entity group to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setOne: groupsAdapter.setOne,
    /**
     * Accepts an array of group entities, and adds or replaces them.
     *
     * @param state store state
     * @param entity groups to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setMany: groupsAdapter.setMany,
    /**
     * Removes all group entities from the store.
     *
     * @param state store state
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeAll: groupsAdapter.removeAll,
    /**
     * Accepts an array of group IDs, and removes each group entity with those IDs if they exist.
     *
     * @param state store state
     * @param entity Group Ids to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeMany: groupsAdapter.removeMany,
    /**
     * Accepts a single group IDs, and removes the group entity with that ID if it exists.
     *
     * @param state store state
     * @param entity Group Id to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeOne: groupsAdapter.removeOne,
    /** Adds group IDs to the processing groups map */
    addProcessingGroups(state, action) {
      action.payload.forEach(groupId => state.fetching.processingGroups[groupId] = true);
    },
    /** Removes group IDs from the processing groups map */
    removeProcessingGroups(state, action) {
      action.payload.forEach(groupId => delete state.fetching.processingGroups[groupId]);
    },
    /**
     * Stores the group ID of the selected group.
     */
    setSelectedGroupId(state, action) {
      state.selectedGroupId = action.payload;
    },
    /** Resets the group store to its initial state. */
    resetGroupsState: () => initialState
  },
  extraReducers(builder) {
    builder.addCase(fetchGroups.pending, state => {
      state.fetching.isFetchingGroups = true;
    }).addCase(fetchGroups.fulfilled, (state, action) => {
      state.fetching.isFetchingGroups = false;

      // The response holds every group of the workspace, so the ones missing from it no longer
      // exist, e.g. because they were deleted by unassigning their last member.
      const fetchedGroupIds = new Set(action.payload.map(({
        id
      }) => id));
      const removedGroupIds = state.ids.filter(groupId => !fetchedGroupIds.has(groupId));
      removedGroupIds.forEach(groupId => {
        delete state.projectsCount[groupId];
        delete state.projectsCountRequestIds[groupId];
      });
      groupsAdapter.removeMany(state, removedGroupIds);

      // Using upsertMany instead of setAll as there might be some groups in shape of group details already in store
      // and setMany will unnecessary remove and replace them
      groupsAdapter.upsertMany(state, action.payload);
    }).addCase(fetchGroups.rejected, state => {
      state.fetching.isFetchingGroups = false;
    }).addCase(fetchGroupDetails.pending, state => {
      state.fetching.isFetchingGroups = true;
    }).addCase(fetchGroupDetails.fulfilled, (state, action) => {
      state.selectedGroupId = action.payload.id;
      groupsAdapter.setOne(state, action.payload);
      state.fetching.isFetchingGroups = false;
    }).addCase(fetchGroupDetails.rejected, state => {
      state.fetching.isFetchingGroups = false;
    }).addCase(addGroup.fulfilled, (state, action) => {
      groupsAdapter.setOne(state, action.payload);
    }).addCase(removeMemberFromGroup.pending, state => {
      state.fetching.isRemovingGroupMember = true;
    }).addCase(removeMemberFromGroup.fulfilled, (state, action) => {
      removeLocalMemberFromGroup(state, action.payload);
      state.fetching.isRemovingGroupMember = false;
    }).addCase(removeMemberFromGroup.rejected, state => {
      state.fetching.isRemovingGroupMember = false;
    }).addCase(removeGroup.pending, state => {
      state.fetching.isRemovingGroup = true;
    }).addCase(removeGroup.fulfilled, state => {
      state.fetching.isRemovingGroup = false;
    }).addCase(removeGroup.rejected, state => {
      state.fetching.isRemovingGroup = false;
    })

    // The cached count is dropped as soon as a refresh starts and is only restored by a
    // successful one, so that a stale count cannot keep a group deletable that gained a project.
    .addCase(fetchGroupProjectsCount.pending, (state, action) => {
      const {
        groupId
      } = action.meta.arg;
      state.projectsCountRequestIds[groupId] = action.meta.requestId;
      delete state.projectsCount[groupId];
    }).addCase(fetchGroupProjectsCount.fulfilled, (state, action) => {
      const {
        groupId
      } = action.payload;
      if (state.projectsCountRequestIds[groupId] !== action.meta.requestId) {
        return;
      }
      state.projectsCount[groupId] = action.payload.projectsCount;
      delete state.projectsCountRequestIds[groupId];
    }).addCase(fetchGroupProjectsCount.rejected, (state, action) => {
      const {
        groupId
      } = action.meta.arg;
      if (state.projectsCountRequestIds[groupId] !== action.meta.requestId) {
        return;
      }
      delete state.projectsCount[groupId];
      delete state.projectsCountRequestIds[groupId];
    }).addCase(updateMemberRoleInGroup.pending, state => {
      state.fetching.isUpdatingGroups = true;
    }).addCase(updateMemberRoleInGroup.fulfilled, (state, action) => {
      updateMemberRoleInGroupStore(state, action.payload);
      state.fetching.isUpdatingGroups = false;
    }).addCase(updateMemberRoleInGroup.rejected, state => {
      state.fetching.isUpdatingGroups = false;
    }).addCase(updateGroupDetails.pending, state => {
      state.fetching.isUpdatingGroups = true;
    }).addCase(updateGroupDetails.fulfilled, (state, action) => {
      state.fetching.isUpdatingGroups = false;
      groupsAdapter.setOne(state, action.payload);
    }).addCase(updateGroupDetails.rejected, (state, _action) => {
      state.fetching.isUpdatingGroups = false;
    });
  }
});
export const {
  setOne,
  setMany,
  removeAll,
  removeMany,
  removeOne,
  addProcessingGroups,
  removeProcessingGroups,
  setSelectedGroupId,
  resetGroupsState
} = groupsSlice.actions;
export const groupsReducer = groupsSlice.reducer;